/**
 * Typography Controls Component
 *
 * Comprehensive typography customization for landing pages.
 * Features:
 * - Font family selection with live previews
 * - Font size controls (heading & body)
 * - Line height sliders
 * - Letter spacing sliders
 * - Font weight selection
 * - Visual feedback
 */

import { Type, AlignLeft, Minus, Plus } from 'lucide-react';
import { cn } from '@/client/lib/utils';
import { Button } from '@/client/components/ui/button';

// =============================================================================
// Font Families
// =============================================================================

export interface FontFamily {
  id:
    | 'inter'
    | 'poppins'
    | 'merriweather'
    | 'system'
    | 'playfair'
    | 'roboto'
    | 'montserrat'
    | 'lora';
  name: string;
  category: 'sans-serif' | 'serif' | 'system';
  description: string;
  preview: string; // Font stack for preview
}

export const FONT_FAMILIES: FontFamily[] = [
  {
    id: 'inter',
    name: 'Inter',
    category: 'sans-serif',
    description: 'Modern and highly readable',
    preview: 'Inter, -apple-system, sans-serif',
  },
  {
    id: 'poppins',
    name: 'Poppins',
    category: 'sans-serif',
    description: 'Friendly and geometric',
    preview: 'Poppins, -apple-system, sans-serif',
  },
  {
    id: 'roboto',
    name: 'Roboto',
    category: 'sans-serif',
    description: 'Clean and professional',
    preview: 'Roboto, -apple-system, sans-serif',
  },
  {
    id: 'montserrat',
    name: 'Montserrat',
    category: 'sans-serif',
    description: 'Bold and modern',
    preview: 'Montserrat, -apple-system, sans-serif',
  },
  {
    id: 'merriweather',
    name: 'Merriweather',
    category: 'serif',
    description: 'Classic and elegant',
    preview: 'Merriweather, Georgia, serif',
  },
  {
    id: 'playfair',
    name: 'Playfair Display',
    category: 'serif',
    description: 'Luxurious and sophisticated',
    preview: 'Playfair Display, Georgia, serif',
  },
  {
    id: 'lora',
    name: 'Lora',
    category: 'serif',
    description: 'Warm and readable',
    preview: 'Lora, Georgia, serif',
  },
  {
    id: 'system',
    name: 'System Default',
    category: 'system',
    description: 'Native to user device',
    preview: 'system-ui, -apple-system, sans-serif',
  },
];

// =============================================================================
// Typography Controls Component
// =============================================================================

export interface TypographySettings {
  fontFamily: FontFamily['id'];
  headingSize?: number; // in rem, default 2.5
  bodySize?: number; // in rem, default 1
  lineHeight?: number; // unitless, default 1.6
  letterSpacing?: number; // in em, default 0
  headingWeight?: number; // 100-900, default 700
  bodyWeight?: number; // 100-900, default 400
}

interface TypographyControlsProps {
  value: TypographySettings;
  onChange: (settings: Partial<TypographySettings>) => void;
}

/**
 * Slider Component
 */
function Slider({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
  description,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (value: number) => void;
  description?: string;
}) {
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className="space-y-2 group">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">{label}</label>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            onClick={() => onChange(Math.max(min, value - step))}
            disabled={value <= min}
            className="h-6 w-6 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
          >
            <Minus className="h-3 w-3" />
          </Button>
          <span className="text-sm font-mono min-w-[3.5rem] text-center text-muted-foreground group-hover:text-foreground group-focus-within:text-foreground transition-colors">
            {(() => {
              // Determine decimal places from step
              const decimals = step >= 1 ? 0 : step >= 0.1 ? 1 : 2;
              return value.toFixed(decimals);
            })()}
            {unit}
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            onClick={() => onChange(Math.min(max, value + step))}
            disabled={value >= max}
            className="h-6 w-6 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Slider Track */}
      <div
        className="relative h-2 bg-muted rounded-full cursor-pointer"
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const percentage = x / rect.width;
          const newValue = min + percentage * (max - min);
          onChange(Math.round(newValue / step) * step);
        }}
      >
        {/* Progress */}
        <div
          className="absolute left-0 top-0 h-full bg-primary rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
        {/* Thumb */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-primary border-2 border-background rounded-full shadow-md transition-all"
          style={{ left: `calc(${percentage}% - 0.5rem)` }}
        />
      </div>

      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
  );
}

/**
 * Font Weight Selector
 */
function FontWeightSelector({
  value,
  onChange,
  label,
}: {
  value: number;
  onChange: (weight: number) => void;
  label: string;
}) {
  const weights = [
    { value: 300, label: 'Light', name: '300' },
    { value: 400, label: 'Regular', name: '400' },
    { value: 500, label: 'Medium', name: '500' },
    { value: 600, label: 'Semibold', name: '600' },
    { value: 700, label: 'Bold', name: '700' },
    { value: 800, label: 'Extrabold', name: '800' },
  ];

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>
      <div className="grid grid-cols-3 gap-2">
        {weights.map((weight) => (
          <button
            key={weight.value}
            type="button"
            onClick={() => onChange(weight.value)}
            className={cn(
              'px-3 py-2 rounded-lg border text-sm transition-colors',
              value === weight.value
                ? 'border-primary bg-primary/10 text-primary font-semibold'
                : 'border-border hover:border-primary/50'
            )}
            style={{ fontWeight: weight.value }}
          >
            {weight.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Main Typography Controls Component
 */
export function TypographyControls({ value, onChange }: TypographyControlsProps) {
  const selectedFont =
    FONT_FAMILIES.find((f) => f.id === value.fontFamily) ||
    FONT_FAMILIES.find((f) => f.id === 'inter')!;

  const handleFontChange = (fontId: FontFamily['id']) => {
    onChange({ fontFamily: fontId });
  };

  return (
    <div className="space-y-6">
      {/* Font Family Selection */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Type className="h-4 w-4 text-muted-foreground" />
          <label className="text-sm font-medium">Font Family</label>
        </div>

        {/* Font Cards */}
        <div className="grid grid-cols-2 gap-3">
          {FONT_FAMILIES.map((font) => (
            <button
              key={font.id}
              type="button"
              onClick={() => handleFontChange(font.id)}
              className={cn(
                'p-3 rounded-xl border-2 text-left transition-all hover:shadow-md',
                value.fontFamily === font.id
                  ? 'border-primary bg-primary/5 ring-2 ring-primary ring-offset-2'
                  : 'border-border hover:border-primary/50'
              )}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">{font.name}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                  {font.category === 'sans-serif'
                    ? 'Sans'
                    : font.category === 'serif'
                      ? 'Serif'
                      : 'System'}
                </span>
              </div>
              <p className="text-2xl mb-1 truncate" style={{ fontFamily: font.preview }}>
                Aa
              </p>
              <p className="text-xs text-muted-foreground">{font.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t" />

      {/* Size Controls */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <AlignLeft className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium">Text Sizes</h4>
        </div>

        <Slider
          label="Heading Size"
          value={value.headingSize || 2.5}
          min={1.5}
          max={4}
          step={0.1}
          unit="rem"
          onChange={(v) => onChange({ headingSize: v })}
          description="Size of main headings (H1)"
        />

        <Slider
          label="Body Size"
          value={value.bodySize || 1}
          min={0.75}
          max={1.5}
          step={0.05}
          unit="rem"
          onChange={(v) => onChange({ bodySize: v })}
          description="Size of paragraph text"
        />
      </div>

      {/* Divider */}
      <div className="border-t" />

      {/* Spacing Controls */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium">Spacing & Weight</h4>

        <Slider
          label="Line Height"
          value={value.lineHeight || 1.6}
          min={1}
          max={2.5}
          step={0.1}
          unit=""
          onChange={(v) => onChange({ lineHeight: v })}
          description="Vertical space between lines"
        />

        <Slider
          label="Letter Spacing"
          value={value.letterSpacing || 0}
          min={-0.05}
          max={0.2}
          step={0.01}
          unit="em"
          onChange={(v) => onChange({ letterSpacing: v })}
          description="Horizontal space between letters"
        />
      </div>

      {/* Divider */}
      <div className="border-t" />

      {/* Font Weight */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium">Font Weight</h4>

        <FontWeightSelector
          label="Heading Weight"
          value={value.headingWeight || 700}
          onChange={(v) => onChange({ headingWeight: v })}
        />

        <FontWeightSelector
          label="Body Weight"
          value={value.bodyWeight || 400}
          onChange={(v) => onChange({ bodyWeight: v })}
        />
      </div>

      {/* Preview */}
      <div className="p-4 border rounded-xl bg-muted/20">
        <p className="text-xs text-muted-foreground mb-3">Preview</p>
        <div style={{ fontFamily: selectedFont.preview }}>
          <h3
            style={{
              fontSize: `${value.headingSize || 2.5}rem`,
              fontWeight: value.headingWeight || 700,
              lineHeight: value.lineHeight || 1.6,
              letterSpacing: `${value.letterSpacing || 0}em`,
            }}
          >
            Heading Text
          </h3>
          <p
            className="mt-2"
            style={{
              fontSize: `${value.bodySize || 1}rem`,
              fontWeight: value.bodyWeight || 400,
              lineHeight: value.lineHeight || 1.6,
              letterSpacing: `${value.letterSpacing || 0}em`,
            }}
          >
            This is how your body text will look. The quick brown fox jumps over the lazy dog.
          </p>
        </div>
      </div>
    </div>
  );
}
